import { useState } from "react";
import { Camera, Calendar, FileSignature, Handshake, Key, Headphones, ShieldCheck, HeartHandshake, ArrowUpRight, type LucideIcon } from "lucide-react";

type Item = { icon: LucideIcon; title: string; body: string };

type Block = {
  eyebrow: string;
  title: string;
  tagline: string;
  items: Item[];
};

const blocks: Block[] = [
  {
    eyebrow: "01 — Market",
    title: "Listed beautifully. Shown effortlessly.",
    tagline: "We make your home impossible to scroll past, and easy for buyers to book.",
    items: [
      { icon: Camera, title: "Pro photography", body: "High-resolution photos that make buyers stop scrolling." },
      { icon: Calendar, title: "Showings on your calendar", body: "Buyers book through a calendar link you control — set your availability, we handle the rest." },
    ],
  },
  {
    eyebrow: "02 — Negotiate",
    title: "Offers reviewed. Paperwork handled.",
    tagline: "Guidance and ready-to-sign templates — for both sides of the deal.",
    items: [
      { icon: Handshake, title: "Offer review & negotiation", body: "Guidance on every offer so you always know your net." },
      { icon: FileSignature, title: "Contracts & disclosures", body: "Seller and buyer templates, DocuSign-ready, fully supported." },
    ],
  },
  {
    eyebrow: "03 — Close",
    title: "Concierge support, all the way home.",
    tagline: "Real humans, 7 days a week — backed by a fee-waive promise and the founder herself.",
    items: [
      { icon: Key, title: "Closing coordination", body: "Title, inspections, and milestones managed end-to-end." },
      { icon: Headphones, title: "Concierge, 7 days a week", body: "Real humans, real answers, every day of the week." },
      { icon: ShieldCheck, title: "Fee-waive guarantee", body: "If we don't deliver, you don't pay. That simple." },
      { icon: HeartHandshake, title: "1-on-1 founder support", body: "Every seller hears directly from our founder. We listen and care for each of you." },
    ],
  },
];

const WhatsIncluded = () => {
  const [active, setActive] = useState(0);

  return (
    <section className="bg-background py-12 md:py-16 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14 space-y-3">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-accent">What you get</span>
          <h2 className="text-3xl md:text-5xl font-display text-foreground">
            Everything a 6% agent does. <span className="accent-italic text-accent">For 0.50%.</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three chapters. One flat fee. Zero corners cut.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {blocks.map((block, i) => {
            const isActive = active === i;
            return (
              <button
                key={block.title}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(i)}
                className={[
                  "group relative text-left rounded-3xl border overflow-hidden",
                  "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
                  "p-7 md:p-8",
                  isActive
                    ? "bg-foreground text-background border-foreground shadow-deep lg:scale-[1.02]"
                    : "bg-brand-warm-50 text-foreground border-border hover:border-accent/50",
                ].join(" ")}
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                {/* Ambient gold orb */}
                <div
                  className={[
                    "pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl transition-opacity duration-700",
                    isActive ? "opacity-60" : "opacity-0",
                  ].join(" ")}
                  style={{ background: "hsl(var(--gold) / 0.5)" }}
                />

                <div className="relative">
                  <div className="flex items-start justify-between mb-6">
                    <span
                      className={[
                        "text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-500",
                        isActive ? "text-accent" : "text-muted-foreground",
                      ].join(" ")}
                    >
                      {block.eyebrow}
                    </span>
                    <ArrowUpRight
                      className={[
                        "h-5 w-5 transition-all duration-500",
                        isActive ? "text-accent translate-x-0 -translate-y-0" : "text-muted-foreground -translate-x-1 translate-y-1 opacity-60",
                      ].join(" ")}
                      strokeWidth={1.5}
                    />
                  </div>

                  <h3 className="font-display text-2xl md:text-[26px] leading-tight mb-3">
                    {block.title}
                  </h3>
                  <p
                    className={[
                      "text-sm leading-relaxed mb-7 transition-colors duration-500",
                      isActive ? "text-background/70" : "text-muted-foreground",
                    ].join(" ")}
                  >
                    {block.tagline}
                  </p>

                  {/* Items: collapsed preview vs expanded list */}
                  <div
                    className={[
                      "grid transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
                      isActive ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 lg:opacity-0",
                    ].join(" ")}
                  >
                    <ul className="overflow-hidden space-y-4">
                      {block.items.map(({ icon: Icon, title, body }, idx) => (
                        <li
                          key={title}
                          className="flex gap-3 items-start translate-y-2 opacity-0 group-[]:translate-y-0"
                          style={{
                            animation: isActive ? `reveal-up 0.6s cubic-bezier(0.22,1,0.36,1) ${idx * 80 + 100}ms both` : undefined,
                          }}
                        >
                          <span
                            className="flex-shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-xl"
                            style={{ background: "hsl(var(--gold) / 0.18)" }}
                          >
                            <Icon className="h-4 w-4 text-accent" strokeWidth={1.75} />
                          </span>
                          <div>
                            <p className="text-sm font-semibold leading-tight mb-1">{title}</p>
                            <p className={["text-xs leading-relaxed", isActive ? "text-background/65" : "text-muted-foreground"].join(" ")}>
                              {body}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Collapsed: show icon row */}
                  <div
                    className={[
                      "flex gap-2 transition-all duration-500",
                      isActive ? "opacity-0 h-0 overflow-hidden" : "opacity-100",
                    ].join(" ")}
                  >
                    {block.items.map(({ icon: Icon, title }) => (
                      <span
                        key={title}
                        className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-background border border-border transition-transform duration-500 group-hover:-translate-y-0.5"
                      >
                        <Icon className="h-4 w-4 text-foreground" strokeWidth={1.5} />
                      </span>
                    ))}
                  </div>

                  {/* Bottom progress hairline */}
                  <div className="mt-7 h-px w-full bg-current/10 relative overflow-hidden">
                    <span
                      className={[
                        "absolute inset-y-0 left-0 transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                        isActive ? "w-full" : "w-0",
                      ].join(" ")}
                      style={{ background: "hsl(var(--gold))" }}
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatsIncluded;
